import clsx from 'clsx';
import { useFormik } from 'formik';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { useAuthContext } from '@/auth/useAuthContext';
import { KeenIcon } from '@/components';
import { useLayout } from '@/providers';
import { useIntl } from 'react-intl';
import { FormattedMessage } from 'react-intl';
import FormInput from '@/components/ui/input/FormInput';
import { generateOtp } from '@/api/UserApi';
import BtnSubmitAuth from '../../../../components/ui/btnSubmitAuth';
import { toast } from 'react-toastify';

const initialValues = {
  email: ''
};

const ResetPassword = () => {
  const intl = useIntl();
  const forgotPasswordSchema = Yup.object().shape({
    email: Yup.string()
      .matches(/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/, intl.formatMessage({ id: 'AUTH.ERROR.INVALID_EMAIL' }))
      .min(3, intl.formatMessage({ id: 'AUTH.ERROR.MIN_LENGTH_EMAIL' }))
      .max(50, intl.formatMessage({ id: 'AUTH.ERROR.MAX_LENGTH_EMAIL' }))
      .required(intl.formatMessage({ id: 'AUTH.ERROR.REQUIRED_EMAIL' }))
  });
  const [loading, setLoading] = useState(false);
  const {
    // requestPasswordResetLink
  } = useAuthContext();
  const {
    currentLayout
  } = useLayout();
  const navigate = useNavigate();
  const formik = useFormik({
    initialValues,
    validationSchema: forgotPasswordSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true);
      setStatus(undefined);
      try {
        // Gọi API generate OTP
        const response = await generateOtp({
          email: values.email,
          code_content: 'forgotpassword'
        });
        if (!response || typeof response.status === 'undefined') {
          setStatus(intl.formatMessage({ id: 'AUTH.ERROR.SERVER_ERROR' }));
          setSubmitting(false);
          return;
        }

        if (response.status === 200) {
          const msg = (response.message || '').toLowerCase();
          if (msg.includes('still valid')) {
            toast.success(intl.formatMessage({ id: 'AUTH.OTP.STILL_VALID' }));
          } else {
            toast.success(intl.formatMessage({ id: 'AUTH.OTP.SENT' }));
          }
          navigate({
            pathname: '/auth/reset-password/enter-code',
            search: `?email=${encodeURIComponent(values.email)}`,
            state: { email: values.email }
          });
          return;
        }

        if (response.status === 422) {
          setStatus(intl.formatMessage({ id: 'AUTH.ERROR.INVALID_EMAIL' }));
        } else if (response.status === 404) {
          setStatus(intl.formatMessage({ id: 'AUTH.ERROR.ACCOUNT_NOT_FOUND', defaultMessage: 'Không tìm thấy tài khoản!' }));
        } else if (response.status === 429) {
          const secondsMatch = (response.message || '').match(/(\d+)/);
          const seconds = secondsMatch ? Number(secondsMatch[1]) : undefined;
          setStatus(
            seconds !== undefined
              ? intl.formatMessage({ id: 'AUTH.OTP.RATE_LIMIT' }, { seconds })
              : intl.formatMessage({ id: 'AUTH.ERROR.TOO_MANY_REQUESTS' })
          );
        } else {
          setStatus(response?.message || response?.error || intl.formatMessage({ id: 'AUTH.ERROR.SERVER_ERROR' }));
        }
      } catch (error) {
        if (!error?.response && (error?.code === 'ERR_NETWORK' || error?.message === 'Network Error' || error?.code === 'ECONNABORTED')) {
          setStatus(intl.formatMessage({ id: 'AUTH.ERROR.NETWORK' }));
        } else if (error?.response) {
          const status = error.response.status;
          const data = error.response.data || {};
          if (status === 422) {
            setStatus(intl.formatMessage({ id: 'AUTH.ERROR.INVALID_EMAIL' }));
          } else if (status === 404) {
            setStatus(intl.formatMessage({ id: 'AUTH.ERROR.ACCOUNT_NOT_FOUND', defaultMessage: 'Không tìm thấy tài khoản!' }));
          } else if (status === 429) {
            const msg = data.message || '';
            const secondsMatch = msg.match(/(\d+)/);
            const seconds = secondsMatch ? Number(secondsMatch[1]) : undefined;
            setStatus(
              seconds !== undefined
                ? intl.formatMessage({ id: 'AUTH.OTP.RATE_LIMIT' }, { seconds })
                : intl.formatMessage({ id: 'AUTH.ERROR.TOO_MANY_REQUESTS' })
            );
          } else {
            setStatus(data?.message || data?.error || intl.formatMessage({ id: 'AUTH.ERROR.SERVER_ERROR' }));
          }
        } else {
          setStatus(error.message || intl.formatMessage({ id: 'AUTH.ERROR.SERVER_ERROR' }));
        }
        setSubmitting(false);
      }
      setLoading(false);
    }
  });
  return <div className="card max-w-[370px] w-full">
    <form className="card-body flex flex-col gap-5 p-10" noValidate onSubmit={formik.handleSubmit}>
      <div className="text-center">
        <h3 className="text-lg font-semibold text-gray-900"><FormattedMessage id="AUTH.YOUR_EMAIL" /></h3>
        <span className="text-2sm text-gray-600 font-medium">
          <FormattedMessage id="AUTH.RESET_PASS.ENTEREMAIL" />
        </span>
      </div>

      <FormInput
        type="email"
        name="email"
        icon={<KeenIcon icon="sms" className="text-lg" />}
        placeholder="example@email.com"

        label={<FormattedMessage id="AUTH.EMAIL" />}
        required
        {...formik.getFieldProps('email')}
        error={formik.errors.email}
        touched={formik.touched.email}
      />

      {/* Inline error display */}
      {formik.status && (
        <div className="text-red-500 text-sm text-center bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {formik.status}
        </div>
      )}

      <div className="flex flex-col gap-5 items-stretch">
        <BtnSubmitAuth
          loading={loading || formik.isSubmitting}
          disabled={loading || formik.isSubmitting}
        >
          {loading || formik.isSubmitting ? (
            <FormattedMessage id="AUTH.PLEASE_WAIT" />
          ) : (
            <FormattedMessage id="AUTH.CONTINUE" />
          )}
        </BtnSubmitAuth>

        <Link to={currentLayout?.name === 'auth-branded' ? '/auth/login' : '/auth/classic/login'} className="flex items-center justify-center text-sm gap-2 text-gray-700 hover:text-[#4AA642]">
          <KeenIcon icon="black-left" />
          <FormattedMessage id="AUTH.BACK_TO_LOGIN" />
        </Link>
      </div>
    </form>
  </div>;
};
export { ResetPassword };